import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CustomerPatternsTab from '@/components/reports/CustomerPatternsTab';   
import PartsOrderedTab from '@/components/reports/PartsOrderedTab';
import ServiceTypesTab from '@/components/reports/ServiceTypesTab';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  FileText, 
  Download, 
  Search, 
  Filter, 
  ChevronLeft, 
  ChevronRight,
  FileSpreadsheet,
  File,
  X,
  Calendar,
  AlertTriangle,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-500'
};

export default function Reports() {
  const navigate = useNavigate();
  const { getSiteParams } = useSite();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Report data
  const [reportData, setReportData] = useState([]);
  const [total, setTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 50;
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [invoiceDateFrom, setInvoiceDateFrom] = useState('');
  const [invoiceDateTo, setInvoiceDateTo] = useState('');
  const [serviceDateFrom, setServiceDateFrom] = useState('');
  const [serviceDateTo, setServiceDateTo] = useState('');
  const [nextServiceFrom, setNextServiceFrom] = useState('');
  const [nextServiceTo, setNextServiceTo] = useState('');
  
  // Export loading
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          
          // Owner (super_admin or shop admin) — reports are site-scoped (iter189)
          if (userData.role !== 'super_admin' && userData.role !== 'admin') {
            setAccessDenied(true);
          }
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const fetchReport = useCallback(async () => {
    if (accessDenied) return;
    
    setDataLoading(true);
    try {
      const params = new URLSearchParams({
        ...getSiteParams(),
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      if (search) params.append('search', search);
      if (invoiceDateFrom) params.append('invoice_date_from', invoiceDateFrom);
      if (invoiceDateTo) params.append('invoice_date_to', invoiceDateTo);
      if (serviceDateFrom) params.append('service_date_from', serviceDateFrom);
      if (serviceDateTo) params.append('service_date_to', serviceDateTo);
      if (nextServiceFrom) params.append('next_service_from', nextServiceFrom);
      if (nextServiceTo) params.append('next_service_to', nextServiceTo);
      
      const response = await fetch(`${BACKEND_URL}/api/reports/service-history?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data.items || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
        setGrandTotal(data.grand_total_amount || 0);
      } else if (response.status === 403) {
        setAccessDenied(true);
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to load report');
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to load report');
    } finally {
      setDataLoading(false);
    }
  }, [page, search, invoiceDateFrom, invoiceDateTo, serviceDateFrom, serviceDateTo, nextServiceFrom, nextServiceTo, getSiteParams, accessDenied]);

  useEffect(() => {
    if (user && !accessDenied) {
      fetchReport();
    }
  }, [user, fetchReport, accessDenied]);

  const buildExportParams = () => {
    const params = new URLSearchParams({ ...getSiteParams() });
    if (search) params.append('search', search);
    if (invoiceDateFrom) params.append('invoice_date_from', invoiceDateFrom);
    if (invoiceDateTo) params.append('invoice_date_to', invoiceDateTo);
    if (serviceDateFrom) params.append('service_date_from', serviceDateFrom);
    if (serviceDateTo) params.append('service_date_to', serviceDateTo);
    if (nextServiceFrom) params.append('next_service_from', nextServiceFrom);
    if (nextServiceTo) params.append('next_service_to', nextServiceTo);
    return params.toString();
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const params = buildExportParams();
      window.open(`${BACKEND_URL}/api/reports/service-history/export/excel?${params}`, '_blank');
      toast.success('Excel export started');
    } catch (error) {
      toast.error('Failed to export Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const params = buildExportParams();
      window.open(`${BACKEND_URL}/api/reports/service-history/export/pdf?${params}`, '_blank');
      toast.success('PDF export started');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setInvoiceDateFrom('');
    setInvoiceDateTo('');
    setServiceDateFrom('');
    setServiceDateTo('');
    setNextServiceFrom('');
    setNextServiceTo('');
    setPage(1);
  };

  const hasActiveFilters = search || invoiceDateFrom || invoiceDateTo || 
    serviceDateFrom || serviceDateTo || nextServiceFrom || nextServiceTo;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-AU');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <Layout user={user}>
        <div className="p-4 md:p-6 lg:p-8" data-testid="reports-access-denied">
          <Card className="p-12 text-center bg-white border border-red-200 rounded-lg">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="h-10 w-10 text-red-600" />
              </div>
              <h2 className="font-bold text-2xl text-slate-900 mb-3">
                Access Denied
              </h2>
              <p className="text-slate-600 mb-6">
                You do not have permission to view reports. This feature is only available to Super Admins.
              </p>
              <Button onClick={() => navigate('/')} variant="outline">
                Return to Dashboard
              </Button>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="reports-container">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-['Chivo'] font-bold text-2xl sm:text-3xl text-slate-900">
            Reports
          </h1>
          <p className="text-slate-500">Service history, customer insights, and lifetime analytics</p>
        </div>

        <Tabs defaultValue="service-history" className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="service-history" className="flex items-center gap-2" data-testid="tab-service-history">
              <FileText className="h-4 w-4" /> Service History
            </TabsTrigger>
            <TabsTrigger value="parts-ordered" className="flex items-center gap-2" data-testid="tab-parts-ordered">
              <FileSpreadsheet className="h-4 w-4" /> Parts Ordered
            </TabsTrigger>
            <TabsTrigger value="service-types" className="flex items-center gap-2" data-testid="tab-service-types">
              <Filter className="h-4 w-4" /> Service Types
            </TabsTrigger>
          </TabsList>

          <TabsContent value="parts-ordered">
            <PartsOrderedTab getSiteParams={getSiteParams} />
          </TabsContent>

          <TabsContent value="service-types">
            <ServiceTypesTab getSiteParams={getSiteParams} />
          </TabsContent>

          <TabsContent value="service-history" className="space-y-6">
            {/* Service-history sub-header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <p className="text-slate-500">{total.toLocaleString()} records found</p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className={hasActiveFilters ? 'border-blue-500 text-blue-600' : ''}
                  data-testid="toggle-filters-btn"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {hasActiveFilters && (
                    <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-700">
                      Active
                    </Badge>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportExcel}
                  disabled={exportingExcel || total === 0}
                  data-testid="export-excel-btn"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  {exportingExcel ? 'Exporting...' : 'Excel'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportPdf}
                  disabled={exportingPdf || total === 0}
                  data-testid="export-pdf-btn"
                >
                  <File className="h-4 w-4 mr-2" />
                  {exportingPdf ? 'Exporting...' : 'PDF'}
                </Button>
              </div>
            </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="p-4 mb-6 bg-slate-50" data-testid="filters-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Date Filters
              </h3>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Invoice Date */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-600">Invoice Date</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={invoiceDateFrom}
                    onChange={(e) => { setInvoiceDateFrom(e.target.value); setPage(1); }}
                    className="flex-1"
                    data-testid="invoice-date-from"
                  />
                  <Input
                    type="date"
                    value={invoiceDateTo}
                    onChange={(e) => { setInvoiceDateTo(e.target.value); setPage(1); }}
                    className="flex-1"
                    data-testid="invoice-date-to"
                  />
                </div>
              </div>
              
              {/* Service Date */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-600">Service Date</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={serviceDateFrom}
                    onChange={(e) => { setServiceDateFrom(e.target.value); setPage(1); }}
                    className="flex-1"
                    data-testid="service-date-from"
                  />
                  <Input
                    type="date"
                    value={serviceDateTo}
                    onChange={(e) => { setServiceDateTo(e.target.value); setPage(1); }}
                    className="flex-1"
                    data-testid="service-date-to"
                  />
                </div>
              </div>
              
              {/* Next Service Date */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-600">Next Service Date</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={nextServiceFrom}
                    onChange={(e) => { setNextServiceFrom(e.target.value); setPage(1); }}
                    className="flex-1"
                    data-testid="next-service-from"
                  />
                  <Input
                    type="date"
                    value={nextServiceTo}
                    onChange={(e) => { setNextServiceTo(e.target.value); setPage(1); }}
                    className="flex-1"
                    data-testid="next-service-to"
                  />
                </div>
              </div>
            </div>
            
            {/* Search */}
            <div className="mt-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by Doc No, Customer, or Rego..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
          </Card>
        )}

        {/* Data Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Doc No</TableHead>
                  <TableHead className="font-semibold">Invoice Date</TableHead>
                  <TableHead className="font-semibold">Service Date</TableHead>
                  <TableHead className="font-semibold">Next Service</TableHead>
                  <TableHead className="font-semibold">Customer</TableHead>
                  <TableHead className="font-semibold">Phone</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Rego</TableHead>
                  <TableHead className="font-semibold">Vehicle</TableHead>
                  <TableHead className="font-semibold text-right">Odometer</TableHead>
                  <TableHead className="font-semibold text-right">Total</TableHead>
                  <TableHead className="font-semibold text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dataLoading ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-12">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                      <p className="mt-2 text-slate-500">Loading report data...</p>
                    </TableCell>
                  </TableRow>
                ) : reportData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-12">
                      <FileText className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                      <p className="text-slate-500">No service history records found</p>
                      {hasActiveFilters && (
                        <Button variant="link" onClick={clearFilters} className="mt-2">
                          Clear filters to see all records
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  reportData.map((item) => (
                    <TableRow key={item.invoice_id} className="hover:bg-slate-50">
                      <TableCell>
                        <button
                          onClick={() => navigate(`/invoices/${item.invoice_id}`)}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          data-testid={`doc-link-${item.invoice_id}`}
                        >
                          {item.doc_no || '-'}
                        </button>
                      </TableCell>
                      <TableCell>{formatDate(item.invoice_date)}</TableCell>
                      <TableCell>{formatDate(item.service_date)}</TableCell>
                      <TableCell>{formatDate(item.next_service_date)}</TableCell>
                      <TableCell className="max-w-[150px] truncate" title={item.customer_name}>
                        {item.customer_name || '-'}
                      </TableCell>
                      <TableCell className="text-sm">{item.customer_phone || '-'}</TableCell>
                      <TableCell className="text-sm max-w-[150px] truncate" title={item.customer_email}>
                        {item.customer_email || '-'}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.vehicle_rego || '-'}</TableCell>
                      <TableCell>
                        {item.vehicle_make || item.vehicle_model 
                          ? `${item.vehicle_make || ''} ${item.vehicle_model || ''}`.trim()
                          : '-'}
                        {item.vehicle_year && <span className="text-slate-400 ml-1">({item.vehicle_year})</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.odometer ? `${item.odometer.toLocaleString()} km` : '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(item.total_amount)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={STATUS_STYLES[item.status] || STATUS_STYLES.draft}>
                          {item.status?.charAt(0).toUpperCase() + item.status?.slice(1) || 'Draft'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Grand Total Summary */}
          {!dataLoading && reportData.length > 0 && (
            <div className="px-4 py-4 bg-orange-50 border-t-2 border-orange-400">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  <span className="font-medium">{total.toLocaleString()}</span> invoices found
                </div>
                <div className="text-right">
                  <span className="text-sm text-slate-600 mr-3">Grand Total (All Filtered Results):</span>
                  <span className="text-2xl font-bold text-green-700">{formatCurrency(grandTotal)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
            <div className="text-sm text-slate-600">
              {total > 0 ? (
                <>Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total.toLocaleString()} records</>
              ) : (
                <>No records</>
              )}
              <span className="ml-2 text-slate-400">({pageSize} per page)</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1}
                data-testid="first-page-btn"
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600 px-2">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="next-page-btn"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                data-testid="last-page-btn"
              >
                Last
              </Button>
            </div>
          </div>
        </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
